import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { MobRatePlanEntry } from '@telialt/common-order-cart/src/entries/MobRatePlanEntry';
import { CommitmentType, EntryWithRatePlan } from '@telialt/models/src/EshopModel';
import { selectCommitment } from '@telialt/utils/src/eshopSelectors';
import { getPeriodFromCode } from '@telialt/utils/src/eshopUtils';
import { formatLithuanianPhoneNumber, withParams } from '@telialt/utils/src/helpers';

import { formatCallsAndSmsAllowance, formatDataAllowance } from '../../helpers/formatAllowance';
import { useCartQuery } from '../../hooks/queries/useCartQuery';

const PHONE_PREFIX = '+370';

export const MobRatePlanEntryConnected: FC<{ entry: EntryWithRatePlan }> = ({ entry }) => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();

    const {
        includedMinuteCount,
        includedSmsCount,
        dataAmount,
        dataAmountUnit,
        dataAmountInEu,
        dataAmountInEuUnit,
        dataAmountInOther,
        dataAmountInOtherUnit,
    } = entry.product?.planCharacteristics || {};

    const callsAndSmsAllowance = formatCallsAndSmsAllowance(includedMinuteCount, includedSmsCount, t);
    const dataDomestic = formatDataAllowance(dataAmount, dataAmountUnit, t);
    const dataEu = formatDataAllowance(dataAmountInEu, dataAmountInEuUnit, t);
    const dataTd1 = formatDataAllowance(dataAmountInOther, dataAmountInOtherUnit, t);
    const dataEuText = dataEu && withParams(t['eshop.order_cart.allowance.data_eu'], { data: dataEu });
    const dataTd1Text = dataTd1 && withParams(t['eshop.order_cart.allowance.data_td1'], { data: dataTd1 });
    const dataForeign = [dataEuText, dataTd1Text].filter(Boolean).join(' + ');

    const commitment = selectCommitment(cart);
    const commitmentPeriod = getPeriodFromCode(commitment || '') || 24;
    const commitmentText =
        commitment === CommitmentType.NoCommitment
            ? t['eshop.order_cart.entry_rate_plan.no_commitment_note']
            : withParams(t['eshop.order_cart.entry_rate_plan.commitment_note'], {
                  period: commitmentPeriod,
              });

    const msisdn = cart?.migratePhoneNumber;
    const phoneNumber = msisdn ? formatLithuanianPhoneNumber(`${PHONE_PREFIX}${msisdn}`) : undefined;

    return (
        <MobRatePlanEntry
            name={entry.product.name}
            monthlyPrice={entry.monthlyPrice}
            callsAndSmsAllowance={callsAndSmsAllowance}
            dataAllowanceDomestic={dataDomestic}
            dataAllowanceForeign={dataForeign}
            phoneNumber={phoneNumber}
            commitmentText={commitmentText}
        />
    );
};
